/**
 * Session context — docs/08-frontend-and-ux.md §1.2: one `useSession()` fed by
 * `GET /api/v1/auth/me` through TanStack Query; a 401 anywhere sends the user to
 * `/login` with `?next=` preserved.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionUser } from '@famtree/shared';
import { ApiError, authApi } from '../api/client.js';

interface SessionContextValue {
  user: SessionUser | null;
  isLoading: boolean;
  /** True once /auth/me has answered either way — the router waits for this. */
  isResolved: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export const sessionQueryKey = ['auth', 'me'] as const;

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: sessionQueryKey,
    queryFn: async () => {
      try {
        return (await authApi.me()).user;
      } catch (error) {
        // Not signed in is a *state*, not a failure: return null so the query
        // resolves and the router can render /login without an error boundary.
        if (error instanceof ApiError && error.problem.status === 401) return null;
        throw error;
      }
    },
    staleTime: 30_000,
    retry: false,
  });

  const signOutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: async () => {
      queryClient.setQueryData(sessionQueryKey, null);
      await queryClient.invalidateQueries();
    },
  });

  const value = useMemo<SessionContextValue>(
    () => ({
      user: query.data ?? null,
      isLoading: query.isLoading,
      isResolved: query.isSuccess || query.isError,
      signOut: async () => {
        await signOutMutation.mutateAsync();
      },
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      },
    }),
    [query.data, query.isLoading, query.isSuccess, query.isError, signOutMutation, queryClient],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside <SessionProvider>');
  return context;
}
